import { Component, OnInit, OnDestroy } from '@angular/core';
import { SubscriptionUtil } from 'src/app/_utilities/subscription';
import { LanguageTranslateService } from 'src/app/_services/language-translate.service';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { UsersService } from 'src/app/_services/admin/users.service';
import { ErrorUtil } from 'src/app/_utilities/error';
import { JoSchedularService } from 'src/app/_services/jo-schedular.service';
import { JobOrdersService } from 'src/app/_services/job-orders.service';
import { NgxSpinnerService } from 'ngx-spinner';

@Component({
  selector: 'app-settings-color-code',
  standalone: false,
  templateUrl: './settings-color-code.component.html',
  styleUrl: './settings-color-code.component.scss'
})
export class SettingsColorCodeComponent extends SubscriptionUtil implements OnInit, OnDestroy {
  translate: any;
  /** Set by ngx-bootstrap initialState from openModal(..., data) */
  data: any;
  searchValue = '';
  userColorList: any[] = [];
  filteredUserList: any[] = [];
  private savingUserIds = new Set<number | string>();

  constructor(
    private languageTranslateService: LanguageTranslateService,
    public modalRef: BsModalRef,
    private userService: UsersService,
    private errorUtil: ErrorUtil,
    private joSchedularService: JoSchedularService,
    private jobOrdersService: JobOrdersService,
    private spinner: NgxSpinnerService
  ) {
    super();
  }

  ngOnInit(): void {
    this.getLanguageData();
  }

  getLanguageData() {
    this.push(this.languageTranslateService.wordSrc$.subscribe(data => {
      if (Object.keys(data).length != 0) {
        this.translate = data;
        this.getUsersList();
      }
    }))
  }

  getUsersList() {
    this.userService.getActiveUsersList().subscribe(
      (res: any) => {
        const users = Array.isArray(res) ? res : (res?.content || res?.data || []);
        this.userColorList = users.map((user: any) => {
          const name = user.fullName || user.name || `${user.firstName || ''} ${user.lastName || ''}`.trim() || '—';
          return {
            id: user.id || user.userId,
            name,
            initials: this.getInitials(name),
            color: this.normalizeHex(user.calendarColorCode || user.colorCode || '#D9D9D9'),
          };
        });
        this.applySearch();
      },
      (err: any) => {
        this.errorUtil.setErrorMessage(
          err.status,
          err.error ? err.error.title : null,
          err.error ? err.error.title : null,
          'error', 2000
        );
      }
    );
  }

  getInitials(name: string): string {
    return String(name)
      .split(' ')
      .map((w: string) => w[0] ?? '')
      .slice(0, 2)
      .join('')
      .toUpperCase();
  }

  applySearch() {
    const query = this.searchValue.trim().toLowerCase();
    this.filteredUserList = !query
      ? [...this.userColorList]
      : this.userColorList.filter((user: any) =>
          user.name?.toLowerCase().includes(query)
        );
  }

  onColorSelected(user: any, color: string) {
    if (!user) return;
    user.color = this.normalizeHex(color);
    this.saveEdit(user);
  }

  private normalizeHex(color: string): string {
    if (!color) return '#D9D9D9';
    return color.startsWith('#') ? color.toUpperCase() : `#${color}`.toUpperCase();
  }

  push(obs: any) { super.push(obs); }

  ngOnDestroy(): void { super.ngOnDestroy(); }

  /** Refresh JO table when opened from table view; otherwise refresh calendar/scheduler. */
  private refreshActiveView() {
    if (this.data?.viewMode === 'table') {
      this.jobOrdersService.refreshList$.emit();
      return;
    }
    this.joSchedularService.refreshList$.emit();
  }

  saveEdit(user: any) {
    if (!user?.id || this.savingUserIds.has(user.id)) return;

    const calendarColorCode = this.normalizeHex(user.color);

    this.spinner.show()
    this.savingUserIds.add(user.id);
    this.userService.updateUserCalendarColor(user.id, calendarColorCode).subscribe({
      next: () => {
        user.color = calendarColorCode;
        this.refreshActiveView();
        this.errorUtil.setErrorMessage(
          200,
          null,
          this.translate?.general?.messages?.savedSuccessfully || 'Color saved successfully',
          'success',
          1500
        );
        this.spinner.hide()
      },
      error: (err: any) => {
        this.spinner.hide()
        this.errorUtil.setErrorMessage(
          err.status,
          err.error ? err.error.title : null,
          err.error ? err.error.title : null,
          'error',
          2000
        );
      },
      complete: () => {
        this.savingUserIds.delete(user.id);
      }
    });
  }
}
